/* eslint-disable tree-shaking/no-side-effects-in-initialization */
import { z } from "zod";

const BBox2D = z.tuple([z.number(), z.number(), z.number(), z.number()]);
type BBox2D = z.infer<typeof BBox2D>;

const BBox3D = z.tuple([
  z.number(),
  z.number(),
  z.number(),
  z.number(),
  z.number(),
  z.number(),
]);
type BBox3D = z.infer<typeof BBox3D>;

const BBox = z.union([BBox2D, BBox3D]);
type BBox = z.infer<typeof BBox>;

const GeoJsonObject = z.object({
  bbox: BBox.optional(),
});

const Position = z.array(z.number());
type Position = z.infer<typeof Position>;

const Point = GeoJsonObject.extend({
  type: z.literal("Point"),
  coordinates: Position,
});
type Point = z.infer<typeof Point>;

const MultiPoint = GeoJsonObject.extend({
  type: z.literal("MultiPoint"),
  coordinates: z.array(Position),
});
type MultiPoint = z.infer<typeof MultiPoint>;

const LineString = GeoJsonObject.extend({
  type: z.literal("LineString"),
  coordinates: z.array(Position),
});
type LineString = z.infer<typeof LineString>;

const MultiLineString = GeoJsonObject.extend({
  type: z.literal("MultiLineString"),
  coordinates: z.array(z.array(Position)),
});
type MultiLineString = z.infer<typeof MultiLineString>;

const Polygon = GeoJsonObject.extend({
  type: z.literal("Polygon"),
  coordinates: z.array(z.array(Position)),
});
type Polygon = z.infer<typeof Polygon>;

const MultiPolygon = GeoJsonObject.extend({
  type: z.literal("MultiPolygon"),
  coordinates: z.array(z.array(z.array(Position))),
});
type MultiPolygon = z.infer<typeof MultiPolygon>;

export const Geometry: z.ZodType<Geometry, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.union([
    Point,
    MultiPoint,
    LineString,
    MultiLineString,
    Polygon,
    MultiPolygon,
    GeometryCollection,
  ]),
);

export type Geometry =
  | Point
  | MultiPoint
  | LineString
  | MultiLineString
  | Polygon
  | MultiPolygon
  | GeometryCollection;

export const GeometryCollection = z.object({
  type: z.literal("GeometryCollection"),
  geometries: z.array(Geometry),
});

export type GeometryCollection = {
  type: "GeometryCollection";
  geometries: Geometry[];
};

export const Feature = GeoJsonObject.extend({
  type: z.literal("Feature"),
  geometry: Geometry,
  id: z.union([z.string(), z.number()]).optional(),
  properties: z.record(
    z.union([z.string(), z.number()]),
    z.union([z.string(), z.number(), z.null()]),
  ),
});
export type Feature = z.infer<typeof Feature>;

export const FeatureCollection = z.object({
  type: z.literal("FeatureCollection"),
  features: z.array(Feature),
});
export type FeatureCollection = z.infer<typeof FeatureCollection>;

export const GeoJson = z.union([Geometry, Feature, FeatureCollection]);
export type GeoJson = z.infer<typeof GeoJson>;
